import createClient, { type Middleware } from 'openapi-fetch'

import type { paths } from '@/api/schema'
import { metaOf, nextCursor, toApiError, unwrap, type Meta } from '@/api/envelope'
import { ApiError, FileTypeError, NetworkError, isAuthExpired } from '@/api/errors'
import { REQUEST_ID_HEADER, newRequestId } from '@/api/request-id'
import { getAccessToken, refreshAccessToken, signOut } from '@/lib/auth/store'
import { sendableAccessToken } from '@/lib/auth/token-expiry'
import { config } from '@/lib/config'

/**
 * The single HTTP boundary.
 *
 * Nothing outside src/api may import this module — components call hooks in
 * features/<domain>/api/, which call the helpers below. This mirrors the
 * backend's own rule that domain apps must never import from api/, and it is
 * enforced by the ESLint boundary rule in eslint.config.js.
 */

const raw = createClient<paths>({ baseUrl: config.isDev ? '' : config.apiBaseUrl })

/**
 * The X-Request-ID the next request should carry, when getData/getPage has
 * one it wants reused. Set and cleared around a synchronous call — see `send`.
 */
let pendingRequestId: string | null = null

/**
 * 1 — tracing: every request carries an X-Request-ID.
 *
 * MUST stay the first middleware registered. openapi-fetch invokes the first
 * onRequest synchronously inside `apiClient.GET(...)`; every later one runs
 * after an await. Only the first can therefore see `pendingRequestId` while
 * `send` still holds it.
 */
const requestIdMiddleware: Middleware = {
  onRequest({ request }) {
    if (!request.headers.has(REQUEST_ID_HEADER)) {
      request.headers.set(REQUEST_ID_HEADER, pendingRequestId ?? newRequestId())
    }
    // Consumed: a thunk that fires two requests gives the id to the first only.
    pendingRequestId = null
    return request
  },
}

/** 2 — auth: attach the in-memory bearer token, renewed if it has expired. */
const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = await sendableAccessToken()
    if (token) request.headers.set('Authorization', `Bearer ${token}`)
    return request
  },
}

/**
 * Session expiries, keyed by the error that reported one, holding the access
 * token the failed request carried. Filled by errorMiddleware, read by
 * `withSessionRenewal`. A WeakMap so the flag stays private to this module
 * rather than becoming a field every ApiError consumer has to ignore.
 */
const expiredToken = new WeakMap<ApiError, string>()

/**
 * 3 — errors: turn any non-2xx into a typed ApiError.
 *
 * This middleware only CLASSIFIES. A 401 is a session expiry — and worth a
 * refresh — only when it is AUTHENTICATION_REQUIRED on a request that carried
 * a bearer token. INVALID_CREDENTIALS and SOCIAL_TOKEN_INVALID are 401s too:
 * refreshing on those would turn a mistyped password into a sign-out.
 *
 * It does not refresh. That decision has one owner, `withSessionRenewal`,
 * because only the caller holds the thunk needed to replay the request — a
 * middleware that refreshed would still have to throw, leaving the caller
 * with a new token and a failed request.
 */
const errorMiddleware: Middleware = {
  async onResponse({ request, response }) {
    if (response.ok) return response

    let body: unknown
    try {
      body = await response.clone().json()
    } catch {
      body = undefined
    }

    const error = toApiError(body, response.status)
    const bearer = request.headers.get('Authorization')?.replace(/^Bearer /, '')
    if (bearer && isAuthExpired(error)) expiredToken.set(error, bearer)
    throw error
  },
}

raw.use(requestIdMiddleware)
raw.use(authMiddleware)
raw.use(errorMiddleware)

export const apiClient = raw

type Call = () => Promise<{ data?: unknown; error?: unknown }>

/**
 * Runs the thunk with `requestId` queued for requestIdMiddleware.
 *
 * `call()` returns as soon as the request is built and the first middleware
 * has run, so the slot is set and cleared within one synchronous stretch and
 * no other request can pick it up. A thunk that awaits something before
 * calling apiClient misses the slot and gets a fresh id — tracing degrades,
 * nothing else does.
 */
const send = (call: Call, requestId: string) => {
  pendingRequestId = requestId
  try {
    return call()
  } finally {
    pendingRequestId = null
  }
}

/**
 * True when a usable access token is (now) in the store.
 *
 * If the current token differs from the one the request carried, a concurrent
 * call has already refreshed — replay on that token instead of spending
 * another refresh. Otherwise refresh; the store collapses concurrent callers
 * onto one request, and signs out itself when the refresh fails.
 */
const renewSession = async (sentToken: string): Promise<boolean> => {
  const current = getAccessToken()
  if (current === null) return false
  if (current !== sentToken) return true
  return refreshAccessToken()
}

/**
 * An expired access token is refreshed and the call replayed ONCE, on the
 * same X-Request-ID so the server logs show one logical request. (An expiry
 * readable off the token is renewed before sending, in authMiddleware; this
 * catches the rest — no readable `exp`, a clock that disagrees, a token
 * mm-be rejected early.)
 *
 *   - refresh fails        → signed out (by the store); the original error is thrown
 *   - replay 401s again    → a token minted seconds ago was rejected, so the
 *                            session is unusable: sign out and throw. Never a
 *                            second refresh — that is how a client loops.
 *   - anything else        → thrown as-is
 */
const withSessionRenewal = async (call: Call) => {
  const requestId = newRequestId()
  try {
    return await send(call, requestId)
  } catch (e) {
    const sentToken = e instanceof ApiError ? expiredToken.get(e) : undefined
    if (sentToken === undefined || !(await renewSession(sentToken))) throw e

    try {
      return await send(call, requestId)
    } catch (replayError) {
      if (isAuthExpired(replayError)) signOut()
      throw replayError
    }
  }
}

/** Result of a list call: unwrapped rows plus the cursor for the next page. */
export interface Page<T> {
  rows: T[]
  meta: Meta
  nextCursor: string | undefined
}

const rethrow = (e: unknown): never => {
  if (e instanceof ApiError || e instanceof NetworkError) throw e
  throw new NetworkError('Could not reach the server.', e)
}

/**
 * 4 — unwrap. Callers receive `data`, never the envelope.
 *
 * openapi-fetch returns `{ data, error }`; the error branch is unreachable
 * here because errorMiddleware throws first, so a present body is a success.
 *
 * `call` must be a thunk that calls apiClient — not a promise — because an
 * expired session replays it.
 */
export const getData = async <T>(call: Call): Promise<T> => {
  try {
    const { data } = await withSessionRenewal(call)
    return unwrap<T>(data)
  } catch (e) {
    return rethrow(e)
  }
}

/**
 * Shape, not `instanceof`: a Blob made by another realm's `Response` (the
 * fetch under a test runner, an iframe) is a Blob no `instanceof Blob` in this
 * one recognises.
 */
const isBlob = (value: unknown): value is Blob =>
  value instanceof Blob ||
  (typeof value === 'object' &&
    value !== null &&
    'size' in value &&
    'type' in value &&
    'arrayBuffer' in value &&
    'slice' in value)

/** `application/PDF; charset=binary` → `application/pdf`. */
const mediaTypeOf = (blob: Blob): string => blob.type.split(';')[0]?.trim().toLowerCase() ?? ''

/**
 * As getData, for an endpoint that answers a FILE rather than the envelope —
 * the course certificate PDF. The thunk must ask openapi-fetch for
 * `parseAs: 'blob'`; a body that is not a Blob means it did not. A file of
 * any type but `mediaType` (parameters such as a charset aside) is a
 * FileTypeError, never handed to the member as if it were the file.
 *
 * Through the same `withSessionRenewal` as every JSON read, so an access
 * token that expired while the member sat on the page is refreshed and the
 * download replayed rather than failing on the first click.
 */
export const getBlob = async (call: Call, mediaType: string): Promise<Blob> => {
  try {
    const { data } = await withSessionRenewal(call)
    if (!isBlob(data)) throw new NetworkError('Response was not a file.')
    if (mediaTypeOf(data) !== mediaType) throw new FileTypeError(mediaType, data.type)
    return data
  } catch (e) {
    return rethrow(e)
  }
}

/** As getData, but keeps `meta` so useInfiniteQuery can page by cursor. */
export const getPage = async <T>(call: Call): Promise<Page<T>> => {
  try {
    const { data } = await withSessionRenewal(call)
    return {
      rows: unwrap<T[] | null>(data) ?? [],
      meta: metaOf(data),
      nextCursor: nextCursor(data),
    }
  } catch (e) {
    return rethrow(e)
  }
}
